"use client";

import { CircleAlert, Clock3, Loader2, UserPlus } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@nitro/ui/button";
import { Dialog } from "@nitro/ui/dialog";
import {
  getRegistrationAvailability,
  getRegistrationPresentation,
  registrationEntryLabel,
} from "../lib/competitionRegistration";

const DEFAULT_ADAPTERS = Object.freeze({});
let registrationDialogModulePromise = null;

function loadRegistrationDialogModule() {
  registrationDialogModulePromise ||= import("./CompetitionRegistrationDialog.js");
  return registrationDialogModulePromise;
}

export function CompetitionRegistrationButton({ adapters = DEFAULT_ADAPTERS, className = "nitro-primary-button", competition }) {
  const router = useRouter();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [registrationState, setRegistrationState] = useState(null);
  const [leagueInviteIntentKey, setLeagueInviteIntentKey] = useState("");
  const leagueInviteIntentRef = useRef(null);
  const leagueInviteIntentSequenceRef = useRef(0);
  const buttonLabel = registrationButtonLabel(competition, registrationState);
  const registrationAvailability = getRegistrationAvailability(competition);
  const registrationPresentation = getRegistrationPresentation(competition);
  const isRegistrationOpen = registrationAvailability.canRegister;
  const registrationHref = registrationEntryHref(pathname, competition);

  function closeModal() {
    setOpen(false);
    leagueInviteIntentRef.current = null;
    setLeagueInviteIntentKey("");
    if (shouldRefresh) {
      setShouldRefresh(false);
      if (typeof adapters?.refresh === "function") adapters.refresh();
      else router.refresh();
    }
  }

  function openRegistration() {
    if (leagueInviteIntentRef.current) return;
    const key = `${competition?.id || competition?.slug || "competition"}:${++leagueInviteIntentSequenceRef.current}`;
    leagueInviteIntentRef.current = { claimed: false, key };
    setLeagueInviteIntentKey(key);
    setOpen(true);
  }

  const claimLeagueInviteIntent = useCallback((key) => {
    const intent = leagueInviteIntentRef.current;
    if (!intent || intent.key !== key || intent.claimed) return false;
    intent.claimed = true;
    return true;
  }, []);

  if (registrationPresentation.managedParticipation) return null;

  if (!isRegistrationOpen) {
    return (
      <button
        className={className}
        disabled
        style={{ minHeight: 44 }}
        title={registrationAvailability.reason || registrationPresentation.statusLabel}
        type="button"
      >
        <Clock3 size={16} />
        {registrationPresentation.statusLabel || buttonLabel}
      </button>
    );
  }

  return (
    <>
      <button className={className} onClick={openRegistration} style={{ minHeight: 44 }} type="button">
        <UserPlus size={16} />
        {buttonLabel}
      </button>
      {open ? (
        <CompetitionRegistrationModal
          adapters={adapters}
          claimLeagueInviteIntent={claimLeagueInviteIntent}
          competition={competition}
          leagueInviteIntentKey={leagueInviteIntentKey}
          onClose={closeModal}
          onLoaded={(data) => {
            if (data?.status) setRegistrationState(data);
          }}
          onRegistered={(data) => {
            if (data?.status) setRegistrationState(data);
            setShouldRefresh(true);
          }}
          registrationHref={registrationHref}
        />
      ) : null}
    </>
  );
}

// Kept as the public export used by fixtures and existing consumers. The
// registration implementation is fetched only after the trigger is opened.
export function CompetitionRegistrationModal(props) {
  const [attempt, setAttempt] = useState(0);
  const [loadState, setLoadState] = useState({ Component: null, error: "" });

  useEffect(() => {
    let active = true;
    setLoadState({ Component: null, error: "" });
    loadRegistrationDialogModule()
      .then((module) => {
        if (active) setLoadState({ Component: module.CompetitionRegistrationDialog, error: "" });
      })
      .catch((error) => {
        if (active) setLoadState({ Component: null, error: error?.message || "No fue posible preparar la inscripción." });
      });
    return () => {
      active = false;
    };
  }, [attempt]);

  if (loadState.Component) {
    const LoadedDialog = loadState.Component;
    return <LoadedDialog {...props} />;
  }

  function retryLoad() {
    registrationDialogModulePromise = null;
    setAttempt((value) => value + 1);
  }

  return (
    <Dialog
      description="Preparando el formulario seguro de inscripción."
      dismissOnBackdrop={false}
      onClose={props.onClose}
      open
      size="sm"
      title={props.competition?.title || "Inscripción"}
    >
      {loadState.error ? (
        <div className="registration-action-panel" role="alert">
          <CircleAlert size={18} />
          <div>
            <strong>No pudimos preparar la inscripción</strong>
            <p>{loadState.error}</p>
            <div className="nitro-inline-actions">
              <Button onClick={retryLoad} size="sm" type="button" variant="secondary">Reintentar</Button>
              <Button onClick={props.onClose} size="sm" type="button" variant="ghost">Cerrar</Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="registration-loading" role="status"><Loader2 size={22} /> Preparando inscripción...</div>
      )}
    </Dialog>
  );
}

function registrationButtonLabel(competition, registrationState = null) {
  const entryLabel = registrationEntryLabel(registrationState);
  if (entryLabel) return entryLabel;
  const registration = getRegistrationPresentation(competition);
  return registration.ctaLabel || competition.ctaLabel || "Ver evento";
}

function registrationEntryHref(pathname, competition) {
  const returnTo = pathname || `/eventos/${competition.slug || ""}`;
  const params = new URLSearchParams({
    return: returnTo,
    intent: "inscripcion",
  });

  if (competition?.slug) {
    params.set("competition", competition.slug);
  }

  return `/registro?${params.toString()}`;
}
